import React, { useEffect, useMemo, useState } from 'react';
import numeral from 'numeral';

import SidebarLeft from '../components/SidebarLeft/SidebarLeft';
import Header from '../components/Header';

const portfolioStorageKey = 'finspace:portfolio:v1';
const watchlistStorageKey = 'finspace:watchlist:v1';

const seedHoldings = [
    {
        ticker: 'AAPL',
        company: 'Apple Inc',
        buyDate: '2022-03-03',
        buyPrice: 108.21,
        assetClass: 'Equity',
        industry: 'Hardware',
        shares: 5,
        currentPrice: 159.96,
    },
    {
        ticker: 'BAC',
        company: 'Bank of America',
        buyDate: '2021-12-06',
        buyPrice: 96.98,
        assetClass: 'Equity',
        industry: 'Banking',
        shares: 8,
        currentPrice: 75.56,
    },
];

const seedWatchlist = [
    { ticker: 'AMZN', target: 185, currentPrice: 172, notes: 'Retail margin' },
    { ticker: 'GOOG', target: 210, currentPrice: 175, notes: 'AI capex' },
    { ticker: 'AMD', target: 230, currentPrice: 214, notes: 'Data center' },
];

const emptyHolding = {
    ticker: '',
    company: '',
    buyDate: '',
    buyPrice: '',
    assetClass: 'Equity',
    industry: '',
    shares: '',
    currentPrice: '',
};

const emptyWatch = {
    ticker: '',
    target: '',
    currentPrice: '',
    notes: '',
};

const formatCurrency = (value) => numeral(value || 0).format('$0,0.00');
const formatPercent = (value) => numeral(value || 0).format('0.00%');

const readStoredList = (key, fallback) => {
    try {
        return JSON.parse(localStorage.getItem(key)) || fallback;
    } catch (error) {
        return fallback;
    }
};

export const calculatePortfolioRows = (holdings) => {
    const rows = holdings.map((holding) => {
        const shares = Number(holding.shares) || 0;
        const buyPrice = Number(holding.buyPrice) || 0;
        const currentPrice = Number(holding.currentPrice) || buyPrice;
        const marketValue = shares * currentPrice;
        const costBasis = shares * buyPrice;
        const gainLoss = marketValue - costBasis;
        return {
            ...holding,
            shares,
            buyPrice,
            currentPrice,
            marketValue,
            costBasis,
            gainLoss,
            gainLossPercent: costBasis ? gainLoss / costBasis : 0,
        };
    });
    const totalValue = rows.reduce((total, row) => total + row.marketValue, 0);
    return rows.map((row) => ({
        ...row,
        portfolioPercent: totalValue ? row.marketValue / totalValue : 0,
    }));
};

export const calculateWatchlistRows = (watchlist) =>
    watchlist.map((item) => {
        const target = Number(item.target) || 0;
        const currentPrice = Number(item.currentPrice) || 0;
        return {
            ...item,
            target,
            currentPrice,
            upside: currentPrice ? (target - currentPrice) / currentPrice : 0,
        };
    });

const Portfolio = () => {
    const [activeTab, setActiveTab] = useState('portfolio');
    const [holdings, setHoldings] = useState(() =>
        readStoredList(portfolioStorageKey, seedHoldings)
    );
    const [watchlist, setWatchlist] = useState(() =>
        readStoredList(watchlistStorageKey, seedWatchlist)
    );
    const [holdingForm, setHoldingForm] = useState(emptyHolding);
    const [watchForm, setWatchForm] = useState(emptyWatch);

    useEffect(() => {
        localStorage.setItem(portfolioStorageKey, JSON.stringify(holdings));
    }, [holdings]);

    useEffect(() => {
        localStorage.setItem(watchlistStorageKey, JSON.stringify(watchlist));
    }, [watchlist]);

    const rows = useMemo(() => calculatePortfolioRows(holdings), [holdings]);
    const watchRows = useMemo(
        () => calculateWatchlistRows(watchlist),
        [watchlist]
    );
    const totalValue = rows.reduce((total, row) => total + row.marketValue, 0);
    const totalGainLoss = rows.reduce((total, row) => total + row.gainLoss, 0);

    const updateHolding = (field, value) => {
        setHoldingForm((current) => ({ ...current, [field]: value }));
    };

    const updateWatch = (field, value) => {
        setWatchForm((current) => ({ ...current, [field]: value }));
    };

    const addHolding = (event) => {
        event.preventDefault();
        if (!holdingForm.ticker.trim()) {
            return;
        }
        setHoldings((current) => [
            ...current,
            {
                ...holdingForm,
                ticker: holdingForm.ticker.trim().toUpperCase(),
                company:
                    holdingForm.company.trim() ||
                    holdingForm.ticker.trim().toUpperCase(),
            },
        ]);
        setHoldingForm(emptyHolding);
    };

    const addWatch = (event) => {
        event.preventDefault();
        if (!watchForm.ticker.trim()) {
            return;
        }
        setWatchlist((current) => [
            ...current,
            {
                ...watchForm,
                ticker: watchForm.ticker.trim().toUpperCase(),
            },
        ]);
        setWatchForm(emptyWatch);
    };

    const removeHolding = (ticker) => {
        setHoldings((current) =>
            current.filter((holding) => holding.ticker !== ticker)
        );
    };

    const removeWatch = (ticker) => {
        setWatchlist((current) =>
            current.filter((item) => item.ticker !== ticker)
        );
    };

    return (
        <div className="page-board">
            <div className="subpage-board">
                <div className="board-section-sidebar">
                    <SidebarLeft />
                </div>
                <main className="board-section-center">
                    <div className="board-header-profile">
                        <Header />
                    </div>
                    <div className="board-toolbar">
                        <div>
                            <h3 className="board-title">My Portfolio</h3>
                            <p className="board-subtitle">
                                {formatCurrency(totalValue)} total value
                            </p>
                        </div>
                        <form className="board-search">
                            <input
                                type="text"
                                className="form-control form-control-md"
                                placeholder="Search Portfolios"
                            />
                            <button
                                className="btn icon-button board-search-button"
                                type="button"
                                aria-label="Search portfolios"
                            >
                                <i className="bi bi-search bi-icon-md" />
                            </button>
                        </form>
                    </div>
                    <div className="portfolio-shell">
                        <div className="portfolio-tabs">
                            {['portfolio', 'watchlist'].map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    className={activeTab === tab ? 'active' : ''}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab === 'portfolio'
                                        ? 'Portfolio'
                                        : 'Watchlist'}
                                </button>
                            ))}
                        </div>
                        <section className="board-card portfolio-hero">
                            <div>
                                <p className="board-eyebrow">Performance</p>
                                <h4>{formatCurrency(totalGainLoss)}</h4>
                                <span
                                    className={
                                        totalGainLoss >= 0
                                            ? 'positive'
                                            : 'negative'
                                    }
                                >
                                    Unrealized gain/loss
                                </span>
                            </div>
                            <div className="allocation-bars">
                                {rows.map((row) => (
                                    <div key={row.ticker}>
                                        <span>{row.ticker}</span>
                                        <div>
                                            <strong
                                                style={{
                                                    width: `${Math.max(
                                                        row.portfolioPercent *
                                                            100,
                                                        4
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                        <em>
                                            {formatPercent(
                                                row.portfolioPercent
                                            )}
                                        </em>
                                    </div>
                                ))}
                            </div>
                        </section>
                        {activeTab === 'portfolio' ? (
                            <>
                                <section className="board-card">
                                    <div className="board-card-header">
                                        <div>
                                            <p className="board-eyebrow">
                                                Holdings
                                            </p>
                                            <h4>Portfolio</h4>
                                        </div>
                                    </div>
                                    <div className="board-table-wrap">
                                        <table className="board-table">
                                            <thead>
                                                <tr>
                                                    <th>Ticker</th>
                                                    <th>Company</th>
                                                    <th>Buy Date</th>
                                                    <th>Buy Price</th>
                                                    <th>Class</th>
                                                    <th>Industry</th>
                                                    <th>Shares</th>
                                                    <th>Market Value</th>
                                                    <th>Portfolio %</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row) => (
                                                    <tr key={row.ticker}>
                                                        <td>{row.ticker}</td>
                                                        <td>{row.company}</td>
                                                        <td>{row.buyDate}</td>
                                                        <td>
                                                            {formatCurrency(
                                                                row.buyPrice
                                                            )}
                                                        </td>
                                                        <td>{row.assetClass}</td>
                                                        <td>{row.industry}</td>
                                                        <td>{row.shares}</td>
                                                        <td>
                                                            {formatCurrency(
                                                                row.marketValue
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatPercent(
                                                                row.portfolioPercent
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="table-icon-button"
                                                                aria-label={`Remove ${row.ticker}`}
                                                                onClick={() =>
                                                                    removeHolding(
                                                                        row.ticker
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-x" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                                <form
                                    className="board-card portfolio-form"
                                    onSubmit={addHolding}
                                >
                                    <h4>Add Holding</h4>
                                    {[
                                        ['ticker', 'Ticker'],
                                        ['company', 'Company'],
                                        ['buyDate', 'Buy Date'],
                                        ['buyPrice', 'Buy Price'],
                                        ['industry', 'Industry'],
                                        ['shares', 'Shares'],
                                        ['currentPrice', 'Current Price'],
                                    ].map(([field, label]) => (
                                        <label key={field}>
                                            <span>{label}</span>
                                            <input
                                                type={
                                                    field.includes('Price') ||
                                                    field === 'shares'
                                                        ? 'number'
                                                        : field === 'buyDate'
                                                        ? 'date'
                                                        : 'text'
                                                }
                                                value={holdingForm[field]}
                                                onChange={(event) =>
                                                    updateHolding(
                                                        field,
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </label>
                                    ))}
                                    <button
                                        className="btn finspace-action"
                                        type="submit"
                                    >
                                        Add
                                    </button>
                                </form>
                            </>
                        ) : (
                            <>
                                <section className="board-card">
                                    <div className="board-card-header">
                                        <div>
                                            <p className="board-eyebrow">
                                                Watchlist
                                            </p>
                                            <h4>Tracked names</h4>
                                        </div>
                                    </div>
                                    <div className="board-table-wrap">
                                        <table className="board-table">
                                            <thead>
                                                <tr>
                                                    <th>Ticker</th>
                                                    <th>Current Price</th>
                                                    <th>Target</th>
                                                    <th>Upside</th>
                                                    <th>Notes</th>
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {watchRows.map((row) => (
                                                    <tr key={row.ticker}>
                                                        <td>{row.ticker}</td>
                                                        <td>
                                                            {formatCurrency(
                                                                row.currentPrice
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatCurrency(
                                                                row.target
                                                            )}
                                                        </td>
                                                        <td
                                                            className={
                                                                row.upside >= 0
                                                                    ? 'positive'
                                                                    : 'negative'
                                                            }
                                                        >
                                                            {formatPercent(
                                                                row.upside
                                                            )}
                                                        </td>
                                                        <td>{row.notes}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="table-icon-button"
                                                                aria-label={`Remove ${row.ticker}`}
                                                                onClick={() =>
                                                                    removeWatch(
                                                                        row.ticker
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-x" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                                <form
                                    className="board-card portfolio-form"
                                    onSubmit={addWatch}
                                >
                                    <h4>Add Watchlist Ticker</h4>
                                    {[
                                        ['ticker', 'Ticker'],
                                        ['currentPrice', 'Current Price'],
                                        ['target', 'Target'],
                                        ['notes', 'Notes'],
                                    ].map(([field, label]) => (
                                        <label key={field}>
                                            <span>{label}</span>
                                            <input
                                                type={
                                                    field === 'currentPrice' ||
                                                    field === 'target'
                                                        ? 'number'
                                                        : 'text'
                                                }
                                                value={watchForm[field]}
                                                onChange={(event) =>
                                                    updateWatch(
                                                        field,
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </label>
                                    ))}
                                    <button
                                        className="btn finspace-action"
                                        type="submit"
                                    >
                                        Add
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Portfolio;
